import { useToasts } from '@applyboard/crystal-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useContext, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { RawApplicationAggregationResponse } from './useGetApplication'
import { getBaseUrl } from '../../utils/request/getBaseUrl'
import { ApplicationResource } from '@applyboard/school-applications-types-lib'
import { InitContext } from '../../config/initializers'
import { ApplicationState } from './types'
import { convertToMinorCurrencyUnits } from '../../utils/currencyUtils'
import { getApplicationFeeAmount } from '../../utils'
import { RawSchoolResponse } from '../useGetSchool'
import { ApplicationSanitized } from '@backend/sanitize/application'

type FlywireActionProps = {
  applicationId: string
  onSuccess: () => void
}

export function useFlywireAction(props: FlywireActionProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const toast = useToasts()
  const context = useContext(InitContext)
  const hiddenButtonRef = useRef<HTMLButtonElement>(null)

  async function handleOnClick() {
    if (!hiddenButtonRef.current) {
      throw new Error('Hidden button ref is not set')
    }

    await queryClient.invalidateQueries({
      queryKey: ['applications', props.applicationId],
    })

    const response = (await queryClient.getQueryData([
      'applications',
      props.applicationId,
    ])) as RawApplicationAggregationResponse

    const updatedApplication = response.data.attributes.application

    if (updatedApplication.attributes?.applicationState !== ApplicationState.DRAFT) {
      toast.negative(
        new Error(
          'Payment in progress: Another payment is already being processed for this application',
        ),
      )
      navigate(`/applications/${props.applicationId}`)
    } else {
      hiddenButtonRef.current?.click()
      props.onSuccess()
    }
  }

  function setFlywireDialogTrigger(
    application: ApplicationResource,
    school: RawSchoolResponse['data'] | null,
  ) {
    const schoolId = application.relationships.school.data.id
    const appId = application.id
    const baseUrl = getBaseUrl()
    const applicationFeeAmount = getApplicationFeeAmount(application as ApplicationSanitized)

    const config = {
      ...context.flywireConfig,

      displayPayerInformation: true,

      // Pre-fill form values
      amount: convertToMinorCurrencyUnits(applicationFeeAmount),

      // Custom fields
      schoolname: school?.attributes?.name,
      application_id: application.attributes?.humanReadableId,

      // Callback configuration
      return_url: `${baseUrl}/applications/new?applicationId=${appId}`,
      callback_id: `${schoolId}#${appId}`,
      callback_version: '2',
      callback_url: `${baseUrl}/api/payments`,
    }

    window.flywire.Checkout.render(config, '#checkout-button')
  }

  return {
    hiddenButtonRef,
    handleOnClick,
    setFlywireDialogTrigger,
  }
}
